import { useId } from "react";
import { useTranslation } from "react-i18next";
import { formatSchemeDay } from "@utils/schemeDay";
import { formatVolume } from "@utils/formatVolume";
import classNames from "classnames";
import { orderBy } from "lodash";
import { Link } from "react-router-dom";
import Loading from "@components/shared/Loading";
import Card from "@components/zenith/ui/Card";
import VolumeBar from "@components/zenith/ui/VolumeBar";
import { useSubscriberLevel0Resources } from "@hooks/query/zenith/useSubscriberLevel0Resources";
import { useSubscriberDeclarations } from "@hooks/query/zenith/useSubscriberDeclarations";
import { zoneVolumes } from "@utils/volumes";
import { useDashboardPeriod } from "./useDashboardPeriod";

const AccountBalancesWidget = () => {
  const { t } = useTranslation();
  const { accountingPeriod, isPeriodLoading, isPeriodFetching, periodNotice } =
    useDashboardPeriod();
  const {
    data: level0Balances,
    isLoading,
    isFetching,
    isError,
  } = useSubscriberLevel0Resources({
    select: (res: any[] = []) => orderBy(res, ["identifier"], ["asc"]),
  });
  const { data: declarations, isFetching: isDeclarationsFetching } =
    useSubscriberDeclarations({
      select: (data: any) => {
        return data?.filter((d: any) => !d.deletedAt);
      },
    });
  // A zone's balance stands at its latest reading this period, else at the period's start; unknown until both have loaded.
  const asAtOf = (zoneId: string) => {
    // The API lists only the active period's readings, newest first.
    const readAt = declarations?.find(
      (d: any) => d.readAt && d.extractionPoint?.level0ResourceId === zoneId,
    )?.readAt;
    return (
      declarations &&
      accountingPeriod &&
      formatSchemeDay(readAt ?? accountingPeriod.periodStart)
    );
  };

  if (isLoading || isPeriodLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  return (
    <Card
      // min-w-0 lets the zone row scroll instead of widening the grid.
      className="min-w-0"
      title={t("subscriber.level0wrs_volumes")}
      busy={isFetching || isDeclarationsFetching || isPeriodFetching}
    >
      {level0Balances?.length ? (
        <ul className="flex gap-3 overflow-x-auto p-4">
          {level0Balances.map((level0: any) => (
            <ZoneCard
              key={level0.id}
              periodNotice={periodNotice}
              zone={level0}
              asAt={asAtOf(level0.id)}
            />
          ))}
        </ul>
      ) : (
        <p className="my-16 text-center text-lg font-light text-primary-2">
          {/* A failed refresh keeps the zones last loaded, so only zones that never loaded are unavailable. */}
          {isError && !level0Balances
            ? t("zenith.dashboard.level0wrs_unavailable")
            : t("level0wrs.no_data")}
        </p>
      )}
    </Card>
  );
};

const ZoneCard: React.FunctionComponent<{
  zone: any;
  /** The day the balance stands at, as dd/MM/yyyy; absent while unknown. */
  asAt?: string;
  periodNotice?: string;
}> = ({ zone, asAt, periodNotice }) => {
  const { t } = useTranslation();
  const nameId = useId();
  const { allocated, used, remaining: balance } = zoneVolumes(zone);

  return (
    <li
      aria-labelledby={nameId}
      className="flex w-52 shrink-0 flex-col rounded-lg border border-gray-200 px-4 py-3.5"
    >
      <div className="text-xs text-gray-500">{t("common.level0wrs")}</div>
      <h3 id={nameId} className="mb-2.5 text-lg font-bold text-primary-3">
        {zone.identifier}
      </h3>
      {periodNotice ? (
        <p className="mb-3 flex-1 text-xs text-gray-500">{periodNotice}</p>
      ) : (
        <>
          <VolumeBar used={used} total={allocated} />
          <div className="mb-3 mt-2.5 flex flex-1 items-end justify-between gap-2">
            <div>
              <div className="text-xxs text-gray-500">
                {asAt
                  ? t("zenith.dashboard.remaining_balance_as_at", {
                      date: asAt,
                    })
                  : t("zenith.dashboard.remaining_balance")}
              </div>
              <div
                className={classNames(
                  "font-bold",
                  balance < 0 ? "text-red-500" : "text-green-600",
                )}
              >
                {formatVolume(balance)}
              </div>
            </div>
          </div>
        </>
      )}
      <Link
        className="self-start rounded border border-primary-1 px-3 py-1.5 text-xs font-semibold text-primary-1 hover:bg-blue-50"
        to={`/zenith/zone_accounts/${zone.id}`}
        aria-label={
          t("zenith.dashboard.view_level0wrs", {
            identifier: zone.identifier,
          }) as string
        }
      >
        {t("common.view")}
      </Link>
    </li>
  );
};

export default AccountBalancesWidget;
